import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import t from "@/lang/lang";
import { LabData, Report } from "@/services/backend/htz/report/types/report";
import {
  updateHtzReportCache,
  PatchRequest,
  useHtzReportPatchMutation,
  useHtzReportShowQuery,
} from "@/services/backend/htz/report/api/report-api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { Button } from "@/shared/components/ui/button";
import { Edit, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { TextInputField } from "@/shared/components/field/input-field";
import { FieldErrors } from "@/shared/components/field/field";
import {
  joinFieldName,
  useFieldErrors,
  useHasFieldErrors,
  useErrValidationFailedContext,
} from "@/shared/lib/errors/err-validation-failed-context";
import { Laboratory } from "@/services/backend/samples/types/laboratory";
import { useSamplesLaboratoryListQuery } from "@/services/backend/samples/api/lab-api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";

export function LaboratoriesCard({ report }: { report: Report }) {
  const { data } = useHtzReportShowQuery({ id: report.id });
  const request: PatchRequest = {
    id: report.id,
    laboratories: report.laboratories,
  };
  const [set] = useOptimisticDebouncedMutation(
    request,
    updateHtzReportCache,
    useHtzReportPatchMutation(),
  );

  const { resetFields } = useErrValidationFailedContext();
  // "laboratories" holds the error of a missing laboratory.
  const { errors, reset: resetLaboratoriesError } =
    useFieldErrors("laboratories");

  if (!data) return;

  const onAdd = (lab: LabData) => {
    if (data.laboratories.some((l) => l.labId === lab.labId)) {
      return;
    }
    const laboratories = [...data.laboratories, lab];
    resetLaboratoriesError();
    set({ ...request, laboratories }, { ...data, laboratories });
  };

  const onLaboratoryChange = (lab: LabData) => {
    const laboratories = data.laboratories.map((l) =>
      l.labId === lab.labId ? lab : l,
    );
    set({ ...request, laboratories }, { ...data, laboratories });
  };

  const onLaboratoryDelete = (lab: LabData) => {
    const laboratories = data.laboratories.filter((l) => l.labId !== lab.labId);
    resetFields(joinFieldName("laboratories", lab.labId));
    set({ ...request, laboratories }, { ...data, laboratories });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="relative">
          <span>{t("Labore")}</span>
          <AddLaboratoryDropdown
            reportId={report.id}
            onAdd={onAdd}
            disabled={isImmutable(report)}
          />
        </CardTitle>
        <CardDescription>
          {t("Daten der Labore, welche Auswertungen gemacht haben.")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("Labor")}</TableHead>
              <TableHead>{t("Aktionen")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.laboratories.map((lab) => (
              <TableRow key={lab.name}>
                <TableCell className="w-full">{lab.name}</TableCell>
                <TableCell className="flex space-x-2 py-1.5 text-right">
                  <PutLaboratoryDialog
                    laboratory={lab}
                    onLaboratoryChange={onLaboratoryChange}
                    disabled={isImmutable(data)}
                  />
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => onLaboratoryDelete(lab)}
                    disabled={isImmutable(data)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <FieldErrors errors={errors} />
      </CardContent>
    </Card>
  );
}

function AddLaboratoryDropdown({
  reportId,
  onAdd,
  disabled,
}: {
  reportId: string;
  onAdd: (lab: LabData) => void;
  disabled: boolean;
}) {
  const { data: labList } = useSamplesLaboratoryListQuery({});

  if (!labList) {
    return null;
  }

  const doAdd = (lab: Laboratory) => {
    if (disabled) return;

    onAdd({
      reportId,
      labId: lab.id,
      name: lab.name,
      street: lab.address.street,
      postalCode: lab.address.postalCode,
      city: lab.address.city,
      phone: "",
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button variant="outline" className="absolute top-0 right-0">
          {t("Labor hinzufügen")}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuGroup>
          {labList.labs.map((lab) => (
            <DropdownMenuItem key={lab.id} onClick={() => doAdd(lab)}>
              {lab.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function PutLaboratoryDialog({
  laboratory,
  onLaboratoryChange,
  disabled,
}: {
  laboratory: LabData;
  onLaboratoryChange: (labs: LabData) => void;
  disabled: boolean;
}) {
  const fieldName = joinFieldName("laboratories", laboratory.labId);
  // The fields are hidden in the dialog, so the trigger shows their errors.
  const { hasErrors } = useHasFieldErrors(fieldName);

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          aria-invalid={hasErrors || undefined}
        >
          <Edit className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <TextInputField
          label={t("Name")}
          id="name"
          text={laboratory.name}
          onTextChange={(name) => {
            onLaboratoryChange({ ...laboratory, name });
          }}
          fieldName={joinFieldName(fieldName, "name")}
          disabled={disabled}
        />
        <TextInputField
          label={t("Straße")}
          id="street"
          text={laboratory.street}
          onTextChange={(street) => {
            onLaboratoryChange({ ...laboratory, street });
          }}
          fieldName={joinFieldName(fieldName, "street")}
          disabled={disabled}
        />
        <TextInputField
          label={t("PLZ")}
          id="postalCode"
          text={laboratory.postalCode}
          onTextChange={(postalCode) => {
            onLaboratoryChange({ ...laboratory, postalCode });
          }}
          fieldName={joinFieldName(fieldName, "postalCode")}
          disabled={disabled}
        />
        <TextInputField
          label={t("Stadt")}
          id="city"
          text={laboratory.city}
          onTextChange={(city) => {
            onLaboratoryChange({ ...laboratory, city });
          }}
          fieldName={joinFieldName(fieldName, "city")}
          disabled={disabled}
        />
        <TextInputField
          label={t("Telefon")}
          id="phone"
          text={laboratory.phone}
          onTextChange={(phone) => {
            onLaboratoryChange({ ...laboratory, phone });
          }}
          fieldName={joinFieldName(fieldName, "phone")}
          disabled={disabled}
        />
      </DialogContent>
    </Dialog>
  );
}
